export type SlaUrgency = "default" | "warning" | "urgent" | "breached";

export interface SlaDeadlineInfo {
  label: string;
  shortLabel: string;
  sublabel: string;
  urgency: SlaUrgency;
}

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const THREE_DAYS_MS = MS_PER_DAY * 3;

const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? `${count} ${singular}` : `${count} ${plural}`;

export const getSlaDeadlineInfo = (
  dueDate: string | null | undefined
): SlaDeadlineInfo | null => {
  if (!dueDate) return null;

  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return null;

  const now = new Date();
  const diffMs = due.getTime() - now.getTime();

  if (diffMs < 0) {
    const overMs = Math.abs(diffMs);
    const overdueDays = Math.floor(overMs / MS_PER_DAY);
    const overdueHours = Math.floor((overMs % MS_PER_DAY) / MS_PER_HOUR);

    let label: string;
    let shortLabel: string;

    if (overdueDays > 0) {
      label = `Overdue by ${pluralize(overdueDays, "day", "days")}`;
      shortLabel = `Overdue by ${pluralize(overdueDays, "day", "days")}`;
    } else {
      label = `Overdue by ${pluralize(Math.max(1, overdueHours), "hour", "hours")}`;
      shortLabel = `Overdue by ${pluralize(Math.max(1, overdueHours), "hour", "hours")}`;
    }

    return {
      label,
      shortLabel,
      sublabel: "SLA deadline breached",
      urgency: "breached",
    };
  }

  if (diffMs < MS_PER_DAY) {
    const hoursLeft = Math.max(1, Math.ceil(diffMs / MS_PER_HOUR));
    return {
      label: `Due in ${pluralize(hoursLeft, "hour", "hours")}`,
      shortLabel: `${hoursLeft}h left`,
      sublabel: "From SLA deadline",
      urgency: "urgent",
    };
  }

  const daysLeft = Math.ceil(diffMs / MS_PER_DAY);

  if (diffMs < THREE_DAYS_MS) {
    return {
      label: `${pluralize(daysLeft, "Day", "Days")} left`,
      shortLabel: `${daysLeft}d left`,
      sublabel: "From SLA deadline",
      urgency: "warning",
    };
  }

  return {
    label: `${pluralize(daysLeft, "Day", "Days")} left`,
    shortLabel: `${daysLeft}d left`,
    sublabel: "From SLA deadline",
    urgency: "default",
  };
};
